import * as React from 'react';

import { numberToPrettyString } from '@cian/utils/lib/shared/number_helpers';
import { plural } from '@cian/utils/lib/shared/plural';
import { mergeStyles } from '@cian/utils/lib/shared/style';

import { IOfferSchema } from '@cian/api-models/offer-card/v1/get-offer-data';
import {
  getOfferType,
  isCommercial,
} from '../../utils/offer_type';

import * as styles from './index.css';

export interface IDistrictData {
  districtTitle?: string;
  regionTitle?: string;
  population?: number;
  avgHouseAge?: number;
  priceSqm?: number;
  priceSqmDiff?: number;
  roomSalePrice?: number;
  roomSalePriceDiff?: number;
  roomRentPrice?: number;
  roomRentPriceDiff?: number;
}

type TDistrictData = IDistrictData | null;

export interface IBtiDistrictProps {
  districtData: TDistrictData;
  roomsCount: number | null;
  offer: IOfferSchema;
}

export class BtiDistrict extends React.Component<IBtiDistrictProps, {}> {
  public render() {
    if (checkBtiDistrictHidden(this.props.districtData)) {
      return null;
    }

    const offerType = getOfferType(this.props.offer);
    const isCommercialCard = isCommercial(offerType);

    let label = '';

    /**
     * Для коммерческой карточки рядом с "Динамика цены за кв.м. за год" выводим пометку - жилая
     */
    if (isCommercialCard) {
      label = 'жилая';
    }

    const {
      avgHouseAge,
      districtTitle,
      population,
      priceSqm,
      priceSqmDiff,
      regionTitle,
      roomRentPrice,
      roomRentPriceDiff,
      roomSalePrice,
      roomSalePriceDiff,
    } = this.props.districtData as IDistrictData;

    return (
      <div className={styles['container']}>
        <div className={styles['title']}>
          О районе
        </div>
        <div className={styles['content']}>
          <div className={styles['column']}>
            <div className={styles['item']}>
              <div className={styles['name']}>Название</div>
              <div className={styles['value']}>{!!districtTitle ? districtTitle : '-'}</div>
            </div>
            <div className={styles['item']}>
              <div className={styles['name']}>Регион</div>
              <div className={styles['value']}>{!!regionTitle ? regionTitle : '-'}</div>
            </div>
            <div className={styles['item']}>
              <div className={styles['name']}>Население</div>
              <div className={styles['value']}>
                {
                  !!population
                    ? `${numberToPrettyString(population)} ${plural(population, ['человек', 'человека', 'человек'])}`
                    : '-'
                }
              </div>
            </div>
            <div className={styles['item']}>
              <div className={styles['name']}>Средний возраст домов</div>
              <div className={styles['value']}>
                {
                  !!avgHouseAge
                    ? `${avgHouseAge} ${plural(avgHouseAge, ['год', 'года', 'лет'])}`
                    : '-'
                }
              </div>
            </div>
            <div className={styles['item']}>
              <div className={styles['name']}>Средняя цена за м<sup>2</sup></div>
              <div className={styles['value']}>
                {
                  !!priceSqm
                    ? `${numberToPrettyString(+priceSqm.toFixed(2))} \u20BD`
                    : '-'
                }
              </div>
            </div>
          </div>
          <div className={styles['column']}>
            <div className={styles['item']}>
              <div className={styles['name']}>Динамика цены за м<sup>2</sup> за год {label}</div>
              <div {...mergeStyles(
                styles['value'],
                !!priceSqmDiff && priceSqmDiff > 0 ? styles['positive'] : styles['negative'],
              )}>
                {
                  !!priceSqmDiff
                    ? `${priceSqmDiff > 0 ? '+' : ''} ${numberToPrettyString(+priceSqmDiff.toFixed(2))} %`
                    : '-'
                }
              </div>
            </div>
            {
              !!this.props.roomsCount &&
                <div className={styles['item']}>
                  <div className={styles['name']}>
                    Средняя цена {this.props.roomsCount > 4
                      ? 'многокомн. кв.'
                      : `${this.props.roomsCount}-комн.кв.`}
                  </div>
                  <div className={styles['value']}>
                    {
                      !!roomSalePrice
                        ? `${numberToPrettyString(+roomSalePrice.toFixed(2))} \u20BD`
                        : '-'
                    }
                  </div>
                </div>
            }
            <div className={styles['item']}>
              <div className={styles['name']}>Динамика ставки за год {label}</div>
              <div {...mergeStyles(
                styles['value'],
                !!roomSalePriceDiff && roomSalePriceDiff > 0 ? styles['positive'] : styles['negative'],
              )}>
                {
                  !!roomSalePriceDiff
                    ? `${roomSalePriceDiff > 0 ? '+' : ''} ${numberToPrettyString(+roomSalePriceDiff.toFixed(2))} %`
                    : '-'
                }
              </div>
            </div>
            {
              !!this.props.roomsCount &&
                <div className={styles['item']}>
                  <div className={styles['name']}>
                    Ставка аренды {this.props.roomsCount > 4
                      ? 'многокомн. кв.'
                      : `${this.props.roomsCount}-комн.кв.`}
                  </div>
                  <div className={styles['value']}>
                    {
                      !!roomRentPrice
                        ? `${numberToPrettyString(+roomRentPrice.toFixed(2))} \u20BD/мес`
                        : '-'
                    }
                  </div>
                </div>
            }
            <div className={styles['item']}>
              <div className={styles['name']}>Динамика ставки за год {label}</div>
              <div {...mergeStyles(
                styles['value'],
                !!roomRentPriceDiff && roomRentPriceDiff > 0 ? styles['positive'] : styles['negative'],
              )}>
                {
                  !!roomRentPriceDiff
                    ? `${roomRentPriceDiff > 0 ? '+' : ''} ${numberToPrettyString(+roomRentPriceDiff.toFixed(2))} %`
                    : '-'
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export function checkBtiDistrictHidden(districtData: TDistrictData): boolean {
  return !districtData;
}
